import { CalendarPlus, Vote, Sparkles } from "lucide-react";

// Build a Google Calendar event URL for one-click add
const buildGcalUrl = ({ title, startDate, endDate, description, location }) => {
  // dates in YYYYMMDD (all-day events)
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${startDate}/${endDate}`,
    details: `${description}\n\nTogether We Grow Ward 3 with Sikaiadah Newton\nhttps://togetherwegrowward3.com`,
    location: location || "Norfolk, Ward 3",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const DATES = [
  {
    id: "early",
    eyebrow: "Vote Early",
    dateLine: "September 18 — October 31, 2026",
    startDay: "18",
    startMonth: "SEP",
    endDay: "31",
    endMonth: "OCT",
    range: true,
    caption:
      "Six weeks of early in-person voting at Norfolk polling locations. Skip the Election Day line — vote on your schedule.",
    gcal: {
      title: "Vote Early — Norfolk Ward 3",
      startDate: "20260918",
      endDate: "20261101", // exclusive end for all-day GCal
      description:
        "Early voting for Norfolk City Council Ward 3 begins today and runs through October 31, 2026. Vote for Si — Sikaiadah Newton.",
      location: "Norfolk, VA — Ward 3 polling locations",
    },
  },
  {
    id: "day",
    eyebrow: "Election Day",
    dateLine: "November 3, 2026",
    startDay: "03",
    startMonth: "NOV",
    range: false,
    caption:
      "The moment Ward 3 has been building toward. Polls open at 6:00 AM and close at 7:00 PM. If you're in line at 7, you still vote.",
    gcal: {
      title: "Election Day — Norfolk Ward 3",
      startDate: "20261103",
      endDate: "20261104",
      description:
        "Election Day for Norfolk City Council Ward 3. Vote for Si — Sikaiadah Newton. Community is our power.",
      location: "Norfolk, VA — Ward 3 polling locations",
    },
  },
];

export const CampaignCalendar = () => {
  return (
    <section
      id="latest"
      data-testid="campaign-calendar-section"
      className="relative bg-royal-deep py-24 lg:py-32 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/3 w-[600px] h-[600px] rounded-full bg-lavender/10 blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#5B8DEF]/10 blur-[120px]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        {/* Header */}
        <div className="mb-14 lg:mb-20 max-w-3xl">
          <p className="text-[11px] tracking-[0.32em] uppercase text-lavender mb-4">
            06 — Latest
          </p>
          <h2
            data-testid="calendar-heading"
            className="font-serif-display text-classy text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-medium"
          >
            The 2026 <span className="italic font-light">Campaign</span> Calendar
          </h2>
          <p className="font-serif-display italic text-lavender text-xl sm:text-2xl lg:text-3xl mt-5">
            Mark your calendar. Bring a neighbor. Vote.
          </p>
          <p className="text-classy-soft/85 text-[15px] lg:text-base leading-relaxed mt-6 max-w-2xl">
            Two dates are all you need to remember. Whether you vote early or
            on Election Day, every vote in Ward 3 grows the movement.
          </p>
        </div>

        {/* Date cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {DATES.map((d, idx) => (
            <article
              key={d.id}
              data-testid={`vote-card-${d.id}`}
              className="group relative bg-gradient-to-b from-[#1B0640]/80 to-[#0E0526]/80 backdrop-blur-xl border border-lavender/20 hover:border-lavender/50 rounded-sm overflow-hidden transition-all duration-500"
            >
              {/* Corner glow */}
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-lavender/10 blur-3xl group-hover:bg-lavender/20 transition-colors duration-700"></div>

              <div className="relative p-8 sm:p-10 lg:p-12">
                {/* Eyebrow badge */}
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-lavender">
                    {d.id === "early" ? (
                      <Sparkles size={14} />
                    ) : (
                      <Vote size={14} />
                    )}
                    {d.eyebrow}
                  </span>
                  <span className="text-classy/25 font-serif-display text-2xl italic">
                    0{idx + 1}
                  </span>
                </div>

                {/* Date tiles */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="text-center border border-lavender/30 bg-royal/40 backdrop-blur px-5 py-4 rounded-sm min-w-[92px]">
                    <p className="text-[10px] tracking-[0.32em] uppercase text-lavender">
                      {d.startMonth}
                    </p>
                    <p className="font-serif-display text-classy text-5xl lg:text-6xl leading-none mt-1">
                      {d.startDay}
                    </p>
                  </div>
                  {d.range && (
                    <>
                      <div className="text-lavender/60 font-serif-display italic text-2xl">
                        —
                      </div>
                      <div className="text-center border border-lavender/30 bg-royal/40 backdrop-blur px-5 py-4 rounded-sm min-w-[92px]">
                        <p className="text-[10px] tracking-[0.32em] uppercase text-lavender">
                          {d.endMonth}
                        </p>
                        <p className="font-serif-display text-classy text-5xl lg:text-6xl leading-none mt-1">
                          {d.endDay}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Full date line */}
                <p
                  data-testid={`vote-card-${d.id}-date`}
                  className="font-serif-display text-classy text-2xl sm:text-3xl lg:text-4xl leading-tight mb-5"
                >
                  {d.dateLine}
                </p>

                {/* Caption */}
                <p className="text-classy-soft/85 text-[14px] lg:text-[15px] leading-relaxed mb-8">
                  {d.caption}
                </p>

                {/* CTA */}
                <a
                  href={buildGcalUrl(d.gcal)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`vote-card-${d.id}-gcal`}
                  className="btn-outline-classy inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] uppercase"
                >
                  <CalendarPlus size={14} /> Add to Calendar
                </a>
              </div>
            </article>
          ))}
        </div>

        {/* Closing line */}
        <div className="text-center mt-16 lg:mt-20">
          <p className="font-serif-display italic text-classy-soft text-lg lg:text-xl mb-6">
            &ldquo;Every vote is a voice. Every voice grows the movement.&rdquo;
          </p>
          <p className="text-classy-soft text-xs tracking-[0.24em] uppercase">
            — Sikaiadah Newton
          </p>
        </div>
      </div>
    </section>
  );
};

export default CampaignCalendar;
